import React, { useState } from 'react';
import { ICellRendererParams } from '@ag-grid-enterprise/all-modules';
import styled from 'styled-components';
import { ConceptLinkerAiLinkerItemStatus, IConceptLinkerAiLinkerItem } from 'types/conceptLinkerAiLinker';
import ConceptLinkerAiLinkerItemsModel from 'models/ConceptLinkerAiLinkerItemsModel';
import ModuleContext from 'CMFW/context/ModuleContext';

const AiLinkerActionRenderer: React.FC<ICellRendererParams> = (params) => {
  const data = params.data as IConceptLinkerAiLinkerItem;
  const itemsModel = React.useContext(ModuleContext) as ConceptLinkerAiLinkerItemsModel;
  const [processing, setProcessing] = useState(false);

  if (!data) return null;

  const isNeedsReview = data.status === ConceptLinkerAiLinkerItemStatus.NEEDS_REVIEW;
  const isReviewed = data.status === ConceptLinkerAiLinkerItemStatus.DONE;

  if (!isNeedsReview && !isReviewed) {
    return null;
  }

  const handleReview = async () => {
    if (processing || isReviewed) return;
    setProcessing(true);
    try {
      await itemsModel.reviewItem(data.id);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <ActionsContainer>
      <ReviewButton
        onClick={handleReview}
        disabled={processing}
        $reviewed={isReviewed}
      >
        {processing ? (
          <i className="fa fa-spinner fa-spin" />
        ) : isReviewed ? (
          <>
            <i className="fa fa-check" style={{ marginRight: '4px' }} />
            Reviewed
          </>
        ) : (
          'Mark Reviewed'
        )}
      </ReviewButton>
    </ActionsContainer>
  );
};

const ActionsContainer = styled.div`
  display: flex;
  gap: 5px;
  justify-content: center;
  align-items: flex-start;
`;

const ReviewButton = styled.button<{ $reviewed?: boolean }>`
  padding: 6px 14px;
  background-color: ${(props) => (props.$reviewed ? '#4caf50' : '#e3f2fd')};
  color: ${(props) => (props.$reviewed ? 'white' : '#1565c0')};
  border: 1px solid ${(props) => (props.$reviewed ? '#4caf50' : '#90caf9')};
  border-radius: 6px;
  cursor: ${(props) => (props.$reviewed ? 'default' : 'pointer')};
  font-size: 13px;
  font-weight: 600;
  transition: all 0.2s ease;
  min-width: 110px;
  text-align: center;
  pointer-events: ${(props) => (props.$reviewed ? 'none' : 'auto')};
  box-shadow: ${(props) => (props.$reviewed ? '0 2px 4px rgba(0,0,0,0.1)' : 'none')};

  &:hover:not(:disabled):not([data-reviewed='true']) {
    background-color: #42a5f5;
    color: white;
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    cursor: wait;
    opacity: 0.5;
  }
`;

export default AiLinkerActionRenderer;
